import React, { useState, useEffect, useCallback } from 'react';
import { X, Loader2, Pencil, Trash2, CheckCircle2, Phone, Mail, User, History } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import ReconcileModal from './ReconcileModal';
import {
    formatBalance, formatDaysAgo, formatInterval, getDifferenceMeta, getReviewStatusMeta
} from '../../utils/reconciliationUtils';
import { formatRecordDate } from '../../utils/dateUtils';

const CompanyHistoryDrawer = ({ company, today, onClose, onChanged }) => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [eventToEdit, setEventToEdit] = useState(null);

    const companyId = company ? company.id : null;

    const fetchEvents = useCallback(async () => {
        if (!companyId) return;
        setIsLoading(true);
        try {
            const response = await api.get(`/reconciliations/companies/${companyId}/events`);
            setEvents(response.data.events || []);
        } catch (error) {
            toast.error('Mutabakat geçmişi yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    const handleSaved = () => {
        fetchEvents();
        if (onChanged) onChanged();
    };

    const handleDelete = async (event) => {
        if (!window.confirm(`${formatRecordDate(event.reconciledDate)} tarihli mutabakat kaydı silinsin mi?\n\nFirmanın son mutabakat tarihi bir önceki kayda döner.`)) {
            return;
        }

        try {
            await api.delete(`/reconciliations/events/${event.id}`);
            toast.success('Mutabakat kaydı silindi.');
            handleSaved();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Kayıt silinemedi.');
        }
    };

    if (!company) {
        return null;
    }

    const status = getReviewStatusMeta(company);
    const last = company.lastEvent;

    return (
        <>
            <div className="fixed inset-0 z-[75] flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
                <div
                    className="flex h-full w-full max-w-xl flex-col bg-dark-card border-l border-dark-border shadow-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-start justify-between gap-3 border-b border-dark-border bg-dark-bg/40 p-5">
                        <div className="min-w-0">
                            <h2 className="truncate text-xl font-bold text-dark-text">{company.name}</h2>
                            <p className="text-sm text-dark-muted">
                                {last
                                    ? `Son mutabakat: ${formatRecordDate(last.reconciledDate)} • ${formatDaysAgo(company.daysSince).toLowerCase()}`
                                    : 'Henüz mutabakat yapılmadı'}
                            </p>
                            <span className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${status.className}`}>
                                {status.label}{status.detail ? ` • ${status.detail}` : ''}
                            </span>
                        </div>
                        <button onClick={onClose} className="rounded-full p-2 text-dark-muted transition-colors hover:bg-dark-hover hover:text-dark-text" aria-label="Kapat">
                            <X size={20} />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto p-5 space-y-5">
                        <div className="grid grid-cols-3 gap-3">
                            <div className="rounded-xl border border-dark-border bg-dark-bg/50 p-3">
                                <p className="text-xs text-dark-muted">Sıklık</p>
                                <p className="mt-1 text-sm font-bold text-dark-text">{formatInterval(company.intervalDays)}</p>
                                {company.usesDefaultInterval && <p className="text-[11px] text-dark-muted">varsayılan</p>}
                            </div>
                            <div className="rounded-xl border border-dark-border bg-dark-bg/50 p-3">
                                <p className="text-xs text-dark-muted">Sonraki</p>
                                <p className="mt-1 text-sm font-bold text-dark-text">{company.dueDate ? formatRecordDate(company.dueDate) : '—'}</p>
                            </div>
                            <div className="rounded-xl border border-dark-border bg-dark-bg/50 p-3">
                                <p className="text-xs text-dark-muted">Toplam kayıt</p>
                                <p className="mt-1 text-sm font-bold text-dark-text">{company.eventCount}</p>
                            </div>
                        </div>

                        {(company.contactName || company.contactPhone || company.contactEmail) && (
                            <div className="space-y-1.5 rounded-xl border border-dark-border bg-dark-bg/50 p-3 text-sm text-dark-text">
                                {company.contactName && <p className="flex items-center gap-2"><User size={14} className="text-dark-muted" /> {company.contactName}</p>}
                                {company.contactPhone && <p className="flex items-center gap-2"><Phone size={14} className="text-dark-muted" /> {company.contactPhone}</p>}
                                {company.contactEmail && <p className="flex items-center gap-2"><Mail size={14} className="text-dark-muted" /> {company.contactEmail}</p>}
                            </div>
                        )}

                        <button
                            onClick={() => { setEventToEdit(null); setIsModalOpen(true); }}
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-primary-500/20 transition-all hover:bg-primary-500"
                        >
                            <CheckCircle2 size={18} /> Mutabakat Yap
                        </button>

                        <div>
                            <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-dark-text">
                                <History size={16} className="text-dark-muted" /> Mutabakat Geçmişi
                            </h3>

                            {isLoading && (
                                <div className="flex items-center justify-center gap-2 p-8 text-dark-muted">
                                    <Loader2 size={18} className="animate-spin" /> Yükleniyor...
                                </div>
                            )}

                            {!isLoading && events.length === 0 && (
                                <p className="rounded-xl border border-dashed border-dark-border p-6 text-center text-sm text-dark-muted">
                                    Henüz mutabakat kaydı yok.
                                </p>
                            )}

                            {!isLoading && events.length > 0 && (
                                <div className="space-y-3">
                                    {events.map((event, index) => {
                                        const diff = getDifferenceMeta(event);

                                        return (
                                            <div key={event.id} className="rounded-xl border border-dark-border bg-dark-bg/40 p-3">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div>
                                                        <p className="text-sm font-semibold text-dark-text">
                                                            {formatRecordDate(event.reconciledDate)}
                                                            {index === 0 && <span className="ml-2 rounded-full border border-transparent bg-primary-500/15 px-2 py-0.5 text-[10px] font-semibold text-primary-500">Son</span>}
                                                        </p>
                                                        <p className="text-xs text-dark-muted">
                                                            {event.createdBy || '—'}
                                                            {event.source === 'migrated' ? ' • aylık kayıttan taşındı' : ''}
                                                            {event.updatedBy ? ` • düzenledi: ${event.updatedBy}` : ''}
                                                        </p>
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-1">
                                                        <button
                                                            onClick={() => { setEventToEdit(event); setIsModalOpen(true); }}
                                                            className="rounded-lg p-1.5 text-dark-muted transition-colors hover:bg-blue-500/10 hover:text-blue-500"
                                                            title="Düzenle"
                                                        >
                                                            <Pencil size={15} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(event)}
                                                            className="rounded-lg p-1.5 text-dark-muted transition-colors hover:bg-red-500/10 hover:text-red-500"
                                                            title="Sil"
                                                        >
                                                            <Trash2 size={15} />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-dark-muted">
                                                    <span>Bizim: <strong className="text-dark-text">{formatBalance(event.ourBalance, event.currency)}</strong></span>
                                                    <span>Karşı: <strong className="text-dark-text">{formatBalance(event.theirBalance, event.currency)}</strong></span>
                                                    {diff.tone !== 'muted' && (
                                                        <span className={`rounded-md border px-2 py-0.5 font-semibold ${diff.className}`}>{diff.label}</span>
                                                    )}
                                                </div>

                                                {event.note && <p className="mt-2 text-xs text-dark-text">{event.note}</p>}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <ReconcileModal
                isOpen={isModalOpen}
                company={company}
                event={eventToEdit}
                today={today}
                onClose={() => setIsModalOpen(false)}
                onDone={handleSaved}
            />
        </>
    );
};

export default CompanyHistoryDrawer;
